import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowLeft, Building2, Loader2, Save } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const getToken = () => localStorage.getItem('admin_token');
const authHeaders = () => ({ headers: { Authorization: 'Bearer ' + getToken() }, withCredentials: true });

export default function AdminBillingSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(API + '/admin/billing/settings', authHeaders());
      setSettings(res.data);
    } catch { toast.error('Eroare la încărcare setări'); }
    finally { setLoading(false); }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const res = await axios.put(API + '/admin/billing/settings', settings, authHeaders());
      setSettings(res.data);
      toast.success('Setări salvate cu succes');
    } catch { toast.error('Eroare la salvare'); }
    finally { setSaving(false); }
  };

  const update = (field, value) => setSettings(prev => ({ ...prev, [field]: value }));

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>;
  if (!settings) return <div className="text-center py-12">Eroare la încărcare</div>;

  return (
    <div className="space-y-5" data-testid="billing-settings-page">
      <div className="flex items-center gap-4">
        <Link to="/admin/invoices"><Button variant="ghost" size="sm"><ArrowLeft className="h-4 w-4 mr-1" />Înapoi la facturi</Button></Link>
        <div className="flex-1">
          <h1 className="text-2xl font-semibold">Setări facturare</h1>
          <p className="text-sm text-slate-500">Configurare date companie, serii facturi și parametri</p>
        </div>
        <Button onClick={saveSettings} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white gap-2" data-testid="save-settings-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}Salvează
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Company details */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base"><Building2 className="h-4 w-4 text-blue-600" />Date companie (furnizor)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div><Label>Denumire companie</Label><Input value={settings.company_name} onChange={e => update('company_name', e.target.value)} data-testid="setting-company-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>CUI</Label><Input value={settings.company_cui} onChange={e => update('company_cui', e.target.value)} data-testid="setting-company-cui" /></div>
              <div><Label>Nr. Reg. Comerțului</Label><Input value={settings.company_reg_number} onChange={e => update('company_reg_number', e.target.value)} data-testid="setting-company-reg" /></div>
            </div>
            <div><Label>Adresă</Label><Input value={settings.company_address} onChange={e => update('company_address', e.target.value)} data-testid="setting-company-address" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Județ</Label><Input value={settings.company_county} onChange={e => update('company_county', e.target.value)} /></div>
              <div><Label>Țara</Label><Input value={settings.company_country} onChange={e => update('company_country', e.target.value)} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Telefon</Label><Input value={settings.company_phone} onChange={e => update('company_phone', e.target.value)} /></div>
              <div><Label>Email</Label><Input value={settings.company_email} onChange={e => update('company_email', e.target.value)} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Cont bancar (IBAN)</Label><Input value={settings.company_bank_account} onChange={e => update('company_bank_account', e.target.value)} data-testid="setting-iban" /></div>
              <div><Label>Banca</Label><Input value={settings.company_bank_name} onChange={e => update('company_bank_name', e.target.value)} /></div>
            </div>
          </CardContent>
        </Card>

        {/* Invoice settings */}
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base">Parametri facturare</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Serie facturi</Label><Input value={settings.invoice_series} onChange={e => update('invoice_series', e.target.value)} data-testid="setting-invoice-series" /></div>
              <div><Label>Următorul număr</Label><Input type="number" value={settings.invoice_next_number} onChange={e => update('invoice_next_number', Number(e.target.value))} data-testid="setting-invoice-next" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Serie chitanțe</Label><Input value={settings.receipt_series} onChange={e => update('receipt_series', e.target.value)} /></div>
              <div><Label>Următorul nr. chitanță</Label><Input type="number" value={settings.receipt_next_number} onChange={e => update('receipt_next_number', Number(e.target.value))} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Serie storno</Label><Input value={settings.storno_series} onChange={e => update('storno_series', e.target.value)} /></div>
            </div>

            <div className="flex items-center justify-between pt-2 border-t">
              <div>
                <Label>Plătitor TVA</Label>
                <p className="text-xs text-slate-500">Activează TVA pe facturile emise</p>
              </div>
              <Switch checked={settings.vat_payer} onCheckedChange={v => update('vat_payer', v)} data-testid="setting-vat-payer" />
            </div>
            {settings.vat_payer && (
              <div><Label>Cotă TVA implicită (%)</Label><Input type="number" value={settings.default_vat_rate} onChange={e => update('default_vat_rate', Number(e.target.value))} /></div>
            )}

            <div className="flex items-center justify-between pt-2 border-t">
              <div>
                <Label>Resetare anuală numere</Label>
                <p className="text-xs text-slate-500">Numerotare de la 1 la fiecare An Nou</p>
              </div>
              <Switch checked={settings.annual_reset} onCheckedChange={v => update('annual_reset', v)} />
            </div>

            <div className="pt-2 border-t">
              <Label>Mențiune factură</Label>
              <Input value={settings.invoice_mention} onChange={e => update('invoice_mention', e.target.value)} data-testid="setting-mention" />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
